/**
 * Everything an item can actually be bought through, counted in one place.
 *
 * ---- Why this exists ----
 *
 * A purchaser deciding how to source a line is choosing between four things, and until now they
 * lived on four different screens: standing contracts (the blanket-order bar), what suppliers
 * have quoted before (the history dialog), what is on our own shelf (the stock column), and who
 * this RFQ would go to (the suppliers column).
 *
 * Nothing showed them together, so the honest default was "send it to RFQ" — including for items
 * already covered by a contract, or sitting in the warehouse. This is the tally that makes the
 * alternatives visible before that decision is taken.
 */

import type { ItemContract, PurchaseHistoryEntry } from './types'

export interface SourcingCounts {
  /** Standing contracts covering this item, from the SELECTED blanket orders. */
  contracts: number
  /** DISTINCT suppliers who have quoted it before — see `summariseSourcing`. */
  quotedSuppliers: number
  /** Individual past quotes, which is a different and less useful number. */
  quotes: number
  /** Sellable stock (shelf minus reserved). `undefined` = not known, which is NOT zero. */
  available: number | undefined
  /** Suppliers this line's RFQ would go to. */
  rfqSuppliers: number
}

export function summariseSourcing(input: {
  contracts: ItemContract[]
  history: PurchaseHistoryEntry[]
  available: number | undefined
  rfqSuppliers: string[]
}): SourcingCounts {
  /*
    Distinct SUPPLIERS, not quotes. One supplier quoting the same item four times over a year is
    one place to buy from; counting four overstates the choice and makes a single-source item look
    competitive.
  */
  const suppliers = new Set(input.history.map((h) => h.supplier).filter((s) => s.trim() !== ''))
  return {
    contracts: input.contracts.length,
    quotedSuppliers: suppliers.size,
    quotes: input.history.length,
    available: input.available,
    rfqSuppliers: input.rfqSuppliers.length,
  }
}

const plural = (n: number, one: string): string => `${n} ${one}${n === 1 ? '' : 's'}`

/**
 * The tally as one line a purchaser can read at a glance.
 *
 * A source with nothing in it is OMITTED rather than printed as a zero — "0 contracts" is noise
 * that pushes the sources that do exist off the line. Unknown stock says nothing at all, because
 * "0 in stock" is a claim we have not earned (`items.get_stock` may simply not have answered).
 *
 * When nothing is available the sentence says so outright: that is the one case where the tally
 * has to speak, because it is the only honest reason to send an RFQ.
 */
export function describeSourcing(counts: SourcingCounts, uom: string): string {
  const parts: string[] = []
  if (counts.contracts > 0) parts.push(plural(counts.contracts, 'contract'))
  if (counts.quotedSuppliers > 0) parts.push(`${plural(counts.quotedSuppliers, 'supplier')} quoted before`)
  // Only a positive, known figure. `undefined` is unknown; `0` and below is nothing to supply.
  if (counts.available !== undefined && counts.available > 0) parts.push(`${counts.available} ${uom} in stock`)
  if (counts.rfqSuppliers > 0) parts.push(`${plural(counts.rfqSuppliers, 'supplier')} on this RFQ`)

  if (parts.length === 0) return 'No contract, no past quote and no stock — this needs an RFQ'
  return parts.join(' · ')
}
